import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AttendanceTime,
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  InfoBox,
  Input,
  LoadingState,
  Toast,
  cn,
  kstDotDateStr,
  kstMonthDayStr,
  type AttendanceKind,
} from '@biznomi/ui-react'
import { manualReasonLabel, isAbsenceRecord, attendanceTypeLabel } from '@biznomi/types'
import {
  getManualPending, approveManual, rejectManual,
  getPendingDevices, getEmployeeDevices, approveDevice, rejectDevice,
  type AdminAttendanceRecord, type PendingDevice,
} from '@/lib/api/admin'
import { approveMessage } from '@/lib/device-approval'
import { useAuth } from '@/lib/auth/store'

/**
 * 승인 대기 — 수기·부재 신청 처리.
 *
 * 자가승인은 서버가 막는다(MANAGER 한정, ADMIN 은 1인 관리자 교착 때문에 제외).
 * 화면에서도 미리 알려준다 — 눌러보고 403 을 받는 것보다 낫다.
 */
export default function Approvals() {
  const qc = useQueryClient()
  const me = useAuth((s) => s.user)
  const [search, setSearch] = useState('')
  const [rejecting, setRejecting] = useState<AdminAttendanceRecord | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [toast, setToast] = useState('')

  /*
   * 🔴 기기 탭은 **ADMIN 전용**이다 (`device.controller.ts:90` — 아니면 403).
   *   이 화면은 `RequireStaff` 라 부서장도 들어온다. 탭을 안 그리는 것만으로는 부족하고
   *   **요청 자체를 안 낸다**(`enabled: isAdmin`). 안 그러면 화면엔 없는데 요청이 나가고,
   *   그 403 이 캡처 스펙의 4xx 수집에 잡혀 다음 사람이 «정상» 으로 오해한다.
   *
   * ⚠ 막는 건 서버다. 여기 두 줄은 거들 뿐이다.
   * ⚠ 가드레일 #5 는 이걸 못 잡는다 — 그건 플랜 축(minPlan)만 본다. 이건 역할 축이다.
   */
  const isAdmin = me?.role === 'ADMIN'
  const [tab, setTab] = useState<'manual' | 'device'>('manual')

  const devQ = useQuery({
    queryKey: ['admin', 'pending-devices'],
    enabled: isAdmin,
    queryFn: async () => {
      const res = await getPendingDevices()
      if (!res.success) throw new Error(res.error ?? '기기 대기 목록을 불러오지 못했습니다')
      return res.data ?? []
    },
  })
  const pendingDevices = devQ.data ?? []

  /*
   * 승인 확인창을 열 때 **그 한 건만** 그 직원의 기기 목록을 부른다.
   * 목록을 그릴 때가 아니라 누른 순간이다 — 대기자가 서른이어도 조회는 한 번이다.
   *
   * 왜 필요한가: «쓰던 기기가 꺼진다» 를 말하려면 그 직원의 활성 기기를 알아야 하는데
   * `/devices/pending` 응답에는 그 사람의 다른 기기가 안 들어 있다. 그리고 활성 기기가
   * **없을 때**가 더 중요하다 — 해제·반려 뒤 재등록이라는 뜻이다 (`device-approval.ts`).
   */
  const [devTarget, setDevTarget] = useState<{ d: PendingDevice; act: 'approve' | 'reject' } | null>(null)
  const [devMsg, setDevMsg] = useState('')

  async function openDeviceConfirm(d: PendingDevice, act: 'approve' | 'reject') {
    setDevTarget({ d, act })
    if (act === 'reject') {
      setDevMsg(`${d.deviceName || '이 폰'}으로는 출퇴근을 찍을 수 없습니다. 본인이 다시 등록하면 여기서 한 번 더 승인해야 합니다.`)
      return
    }
    setDevMsg('확인하는 중…')
    const res = await getEmployeeDevices(d.employeeId)
    setDevMsg(approveMessage(d.employee.name, d, res.success ? res.data ?? [] : []))
  }

  async function doDeviceAction() {
    if (!devTarget) return
    const { d, act } = devTarget
    setDevTarget(null)
    const res = act === 'approve' ? await approveDevice(d.id) : await rejectDevice(d.id)
    if (!res.success) {
      setToast(res.error ?? (act === 'approve' ? '승인하지 못했습니다' : '반려하지 못했습니다'))
      return
    }
    // 같은 직원의 대기 기기가 둘이면 앞 것이 «승인됨(비활성)» 으로 바뀐다. 다시 불러 보여준다
    qc.invalidateQueries({ queryKey: ['admin'] })
    setToast(act === 'approve' ? '승인했습니다' : '반려했습니다')
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'manual-pending'],
    queryFn: async () => {
      const res = await getManualPending()
      if (!res.success) throw new Error(res.error ?? '승인 대기 목록을 불러오지 못했습니다')
      return res.data ?? []
    },
  })

  const approve = useMutation({
    mutationFn: (id: string) => approveManual(id),
    onSuccess: (res) => {
      if (!res.success) {
        setToast(res.error ?? '승인에 실패했습니다')
        return
      }
      setToast('승인했습니다')
      qc.invalidateQueries({ queryKey: ['admin'] })
    },
  })

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => rejectManual(id, reason),
    onSuccess: (res) => {
      if (!res.success) {
        setToast(res.error ?? '반려에 실패했습니다')
        return
      }
      setRejecting(null)
      setRejectReason('')
      setToast('반려했습니다')
      qc.invalidateQueries({ queryKey: ['admin'] })
    },
  })

  const rows = useMemo(() => {
    const q = search.trim()
    if (!q) return data ?? []
    return (data ?? []).filter(
      (r) =>
        r.employee?.name?.includes(q) ||
        r.employee?.employeeNumber?.includes(q) ||
        r.departmentDisplay?.includes(q),
    )
  }, [data, search])

  /** 부서장은 본인 신청을 스스로 승인할 수 없다 */
  function blockedSelf(r: AdminAttendanceRecord): boolean {
    return me?.role === 'MANAGER' && r.employeeId === me.userId
  }

  if (isLoading) return <LoadingState />
  if (error) return <InfoBox tone="danger">{(error as Error).message}</InfoBox>

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-bold">승인 대기</h1>
        <p className="mt-1 text-sm text-slate-500">
          수기 {data?.length ?? 0}건{isAdmin ? ` · 기기 ${pendingDevices.length}건` : ''}
        </p>
      </header>

      {/* 기기 탭은 ADMIN 에게만. 부서장에게는 탭 자체가 없다 (위 주석 참조) */}
      {isAdmin && (
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
          {([['manual', `수기 ${data?.length ?? 0}`], ['device', `기기 ${pendingDevices.length}`]] as const).map(
            ([k, label]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                className={cn(
                  'flex-1 rounded-md py-2 text-sm font-medium',
                  tab === k ? 'bg-white shadow-sm' : 'text-slate-500',
                )}
              >
                {label}
              </button>
            ),
          )}
        </div>
      )}

      {isAdmin && tab === 'device' ? (
        <DevicePending
          rows={pendingDevices}
          loading={devQ.isLoading}
          onAct={(d, act) => void openDeviceConfirm(d, act)}
        />
      ) : (
      <>
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="이름·사번·부서로 찾기"
        className="max-w-sm"
      />

      {rows.length === 0 ? (
        <div className="rounded-card bg-card">
          <EmptyState title="처리할 신청이 없습니다." icon="inbox" />
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => {
            const absence = isAbsenceRecord(r)
            const selfBlocked = blockedSelf(r)
            return (
              <li key={r.id} className="rounded-card bg-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-semibold">{r.employee?.name ?? '(이름 없음)'}</span>
                      {r.employee?.employeeNumber && (
                        <span className="text-xs text-slate-400">{r.employee.employeeNumber}</span>
                      )}
                      {r.departmentDisplay && (
                        <Badge variant="outline">{r.departmentDisplay}</Badge>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-1.5">
                      {absence ? (
                        <Badge variant="purple">{manualReasonLabel(r.manualReason) || '부재'}</Badge>
                      ) : (
                        <>
                          <Badge variant={r.type === 'CHECKIN' ? 'info' : 'default'}>
                            {attendanceTypeLabel(r.type)}
                          </Badge>
                          <Badge variant="outline">{manualReasonLabel(r.manualReason)}</Badge>
                        </>
                      )}
                      <span className="text-sm text-slate-500">{kstMonthDayStr(r.timestamp)}</span>
                      {!absence && (
                        <AttendanceTime
                          at={new Date(r.timestamp)}
                          kind={r.type as AttendanceKind}
                        />
                      )}
                    </div>

                    {/* 신청 사유는 자르지 않는다 — 이게 판단 근거다 (DCMC 16-1) */}
                    {r.manualNote && (
                      <p className="whitespace-pre-line text-sm text-slate-600">{r.manualNote}</p>
                    )}
                  </div>

                  <div className="flex shrink-0 gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={selfBlocked}
                      onClick={() => setRejecting(r)}
                    >
                      반려
                    </Button>
                    <Button
                      size="sm"
                      disabled={selfBlocked}
                      loading={approve.isPending && approve.variables === r.id}
                      onClick={() => approve.mutate(r.id)}
                    >
                      승인
                    </Button>
                  </div>
                </div>

                {selfBlocked && (
                  <p className="mt-2 text-xs text-slate-400">
                    본인이 낸 신청은 직접 처리할 수 없습니다. 최종관리자에게 요청하세요.
                  </p>
                )}
              </li>
            )
          })}
        </ul>
      )}
      </>
      )}

      {/* 기기 승인·반려 확인 — 문구는 device-approval.ts 가 만든다 (직원 상세와 같은 문장) */}
      {devTarget && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setDevTarget(null)}
          title={devTarget.act === 'approve' ? '이 기기를 승인할까요?' : '이 기기를 반려할까요?'}
          message={devMsg}
          confirmText={devTarget.act === 'approve' ? '승인' : '반려'}
          destructive={devTarget.act === 'reject'}
          onConfirm={() => void doDeviceAction()}
        />
      )}

      {/* 반려 사유는 필수다 — 직원이 그대로 보는 유일한 설명이라 빈 반려는 막는다 */}
      <Dialog
        open={rejecting !== null}
        onOpenChange={(o) => {
          if (!o) {
            setRejecting(null)
            setRejectReason('')
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>반려 사유를 알려주세요</DialogTitle>
            <DialogDescription>
              {rejecting?.employee?.name ?? ''} 님이 기록 화면에서 이 문구를 그대로 보게 됩니다.
            </DialogDescription>
          </DialogHeader>

          <Input
            autoFocus
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            maxLength={200}
            placeholder="예: 해당 날짜 근무 기록이 없습니다"
          />

          <DialogFooter>
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => {
                setRejecting(null)
                setRejectReason('')
              }}
              disabled={reject.isPending}
            >
              취소
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              disabled={!rejectReason.trim()}
              loading={reject.isPending}
              onClick={() => {
                if (!rejecting || !rejectReason.trim()) return
                reject.mutate({ id: rejecting.id, reason: rejectReason.trim() })
              }}
            >
              반려
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {toast && <Toast message={toast} variant="success" onClose={() => setToast('')} />}
    </div>
  )
}

/**
 * 승인 대기 기기 목록.
 *
 * 왜 있나: 서버에 `GET /api/devices/pending` 이 있는데 **부르는 화면이 없었다.**
 * 관리자가 «누가 문 앞에 서 있나» 를 알 방법이 없어서, 직원이 전화로 알려 주면 그 사람
 * 상세를 열어 승인했다. 1기기 1사용자가 대리출근을 막는 뼈대인데 그 관문에 창이 없었다.
 *
 * 오래 기다린 순서로 온다 (서버가 `createdAt asc`).
 */
function DevicePending({
  rows,
  loading,
  onAct,
}: {
  rows: PendingDevice[]
  loading: boolean
  onAct: (d: PendingDevice, act: 'approve' | 'reject') => void
}) {
  if (loading) return <LoadingState />
  if (rows.length === 0) {
    return (
      <div className="rounded-card bg-card">
        <EmptyState title="기다리는 기기가 없습니다." icon="inbox" />
      </div>
    )
  }
  return (
    <ul className="space-y-2">
      {rows.map((d) => (
        <li key={d.id} className="rounded-card bg-card p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-semibold">{d.employee.name}</span>
                {d.employee.employeeNumber && (
                  <span className="text-xs text-slate-400">{d.employee.employeeNumber}</span>
                )}
                {d.employee.department?.name && (
                  <Badge variant="outline">{d.employee.department.name}</Badge>
                )}
              </div>
              <p className="truncate text-sm">{d.deviceName || '이름 없는 기기'}</p>
              <p className="text-xs text-slate-400">
                {[d.platform, `등록 ${kstDotDateStr(d.createdAt)}`].filter(Boolean).join(' · ')}
              </p>
            </div>
            <div className="flex shrink-0 gap-1.5">
              <Button size="sm" onClick={() => onAct(d, 'approve')}>승인</Button>
              <Button variant="secondary" size="sm" onClick={() => onAct(d, 'reject')}>반려</Button>
            </div>
          </div>
        </li>
      ))}
    </ul>
  )
}
